// Updated AssignedServiceOrders.tsx with server-side filtering
import { useMemo, useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  UserPlus,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  RefreshCw,
  Printer,
} from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { ReassignTechnicianModal } from "./reassign/ReassignTechnicianModal";
import { useNavigate } from "react-router-dom";

// Updated imports - using new filtering hooks
import {
  useAssignedServiceOrdersExport,
  useAssignedServiceOrdersWithFilters,
  useAssignedSummary,
  useDownloadReport,
} from "../../hooks";
import { serviceOrderFilters } from "../../types";

// Import existing components
import { ServiceOrderFilterPanel } from "../../../dispatcher/components/filters/filters";
import {
  getPriorityColor,
  getStatusColor,
  formatDate,
  formatDateTime,
} from "../../../utils";
import { GenericTable } from "@shared/components/GenericTable";
import { TransformedServiceOrder } from "../../types";
import { toast } from "@shared/hooks/use-toast";
import { TableColumn } from "@shared/types/table";

export function AssignedServiceOrders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [selectedOrderForReassign, setSelectedOrderForReassign] =
    useState<TransformedServiceOrder | null>(null);

  // =================== API DATA FETCHING WITH FILTERS ===================
  const {
    serviceOrders,
    isLoading,
    error,
    pagination,
    currentFilters,
    handleSearch,
    handlePageChange,
    handleApplyFilters,
    handleResetFilters,
    refetch,
  } = useAssignedServiceOrdersWithFilters();
  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
  } = useAssignedSummary();
  const downloadCSV = useDownloadReport();

  const { exportCSV, isExporting } =
    useAssignedServiceOrdersExport(currentFilters);

  const handleExport = () => {
    exportCSV();
  };

  // Parse current filter values from URL for display
  const searchQuery = searchParams.get("query") || "";
  const statusFilter = searchParams.get("status") || "all";
  const serviceAssociationFilter =
    searchParams.get("serviceAssociation") || "all";
  const priorityFilter = searchParams.get("priority") || "all";
  const areaFilter = searchParams.get("area") || "all";

  // =================== KPI CALCULATIONS ===================
  const kpis = useMemo(() => {
    return {
      totalAssigned: summaryData?.assigned || 0,
      acceptedCount: summaryData?.accepted || 0,
      inProgressCount: summaryData?.inProgress || 0,
      refusedCount: summaryData?.refused || 0,
    };
  }, [summaryData]);

  // KPI Cards configuration using QuickStatsCard
  const assignedServiceOrderKpiCards = [
    {
      title: "Assigned Service Orders",
      count: kpis.totalAssigned,
      trendSummary: "Service orders currently assigned to field technicians",
      trendDirection: "up" as const,
      icon: <UserPlus className="h-8 w-8 text-blue-500" />,
    },
    {
      title: "Accepted Service Orders",
      count: kpis.acceptedCount,
      trendSummary: "Orders accepted by technicians and ready for execution",
      trendDirection: "up" as const,
      icon: <CheckCircle className="h-8 w-8 text-green-500" />,
    },
    {
      title: "In Progress Service Orders",
      count: kpis.inProgressCount,
      trendSummary: "Orders currently being executed in the field",
      trendDirection: "up" as const,
      icon: <Clock className="h-8 w-8 text-orange-500" />,
    },
    {
      title: "Refused Service Orders",
      count: kpis.refusedCount,
      trendSummary:
        "Orders declined by assigned technicians requiring reassignment",
      trendDirection: "down" as const,
      icon: <XCircle className="h-8 w-8 text-red-500" />,
    },
  ];

  useEffect(() => {}, [location.pathname]);

  // =================== TABLE CONFIGURATION ===================
  const tableColumns: TableColumn<any>[] = [
    {
      key: "soId",
      header: "SO ID",
      sortable: true,
      render: (value: string) => (
        <span className="text-blue-600 font-medium">{value}</span>
      ),
    },
    {
      key: "soName",
      header: "SO Name",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-900 font-medium">{value}</span>
      ),
    },
    {
      key: "referenceNo",
      header: "Reference No.",
      render: (value: string) => (
        <span className="font-mono text-gray-700">{value}</span>
      ),
    },
    {
      key: "technician",
      header: "Technician",
      sortable: true,
      render: (value: any) => (
        <div>
          <div className="font-medium text-gray-900">{value.name}</div>
          <div className="text-sm text-gray-500">({value.id})</div>
        </div>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      sortable: true,
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) => getPriorityColor(value),
      },
    },
    {
      key: "areaName",
      header: "Area",
      sortable: true,
    },
    {
      key: "subAreaName",
      header: "Sub Area",
      sortable: true,
    },
    {
      key: "serviceAssociation",
      header: "Service Association",
      sortable: true,
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) => getStatusColor(value),
      },
    },
    {
      key: "createdOn",
      header: "Created On",
      sortable: true,
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "assignDate",
      header: "Assigned On",
      sortable: true,
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "eta",
      header: "ETA",
      sortable: true,
      render: (value: string) => {
        const getEtaColor = (eta: string) => {
          const etaDate = new Date(eta);
          const now = new Date();
          const diffHours =
            (etaDate.getTime() - now.getTime()) / (1000 * 60 * 60);

          if (diffHours < 0) return "text-red-600 font-semibold";
          if (diffHours < 2) return "text-orange-600 font-medium";
          return "text-green-600";
        };

        return <span className={getEtaColor(value)}>{value}</span>;
      },
    },
  ];

  // GenericTable actions configuration
  const tableActions = [
    {
      label: "View Details",
      onClick: (row: any) => handleViewClick(row),
      icon: "Eye" as const,
      variant: "ghost" as const,
    },
    {
      label: "Reassign Technician",
      onClick: (row: any) => handleReassignClick(row),
      icon: "RefreshCw" as const,
      variant: "ghost" as const,
    },
    {
      label: "Print Report",
      onClick: (order: TransformedServiceOrder) =>
        downloadCSV.mutateAsync({ reportId: order.id, tabName: "assigned" }),
      icon: "Printer" as const,
      variant: "ghost" as const,
    },
  ];

  // =================== EVENT HANDLERS ===================
  const handleReassignClick = (order: TransformedServiceOrder) => {
    setSelectedOrderForReassign(order);
    setReassignModalOpen(true);
  };

  const handleViewClick = (order: any) => {
    const association = order.serviceAssociation?.toLowerCase();

    switch (association) {
      case "meter":
        navigate(
          `/service-orders/dispatcher/assigned-meter-service-order/${order.id}`
        );
        break;
      case "consumer":
        navigate(
          `/service-orders/dispatcher/assigned-consumer-service-order/${order.id}`
        );
        break;
      case "asset":
        navigate(
          `/service-orders/dispatcher/assigned-asset-service-order/Asset/${order.id}`
        );
        break;
      case "facility":
        navigate(
          `/service-orders/dispatcher/assigned-asset-service-order/Facility/${order.id}`
        );
        break;
      case "system":
        navigate(
          `/service-orders/dispatcher/assigned-asset-service-order/System/${order.id}`
        );
        break;
      case "network":
        navigate(
          `/service-orders/dispatcher/assigned-asset-service-order/Network/${order.id}`
        );
        break;
      default:
        navigate(
          `/service-orders/dispatcher/assigned-meter-service-order/${order.id}`
        );
        break;
    }
  };

  // Create current filters object for filterPanel
  const filterPanelFilters: Partial<serviceOrderFilters> = useMemo(
    () => ({
      status: statusFilter !== "all" ? statusFilter : undefined,
      serviceAssociation:
        serviceAssociationFilter !== "all"
          ? serviceAssociationFilter
          : undefined,
      priority: priorityFilter !== "all" ? priorityFilter : undefined,
      area: areaFilter !== "all" ? areaFilter : undefined,
      search_data: searchQuery || undefined,
    }),
    [
      statusFilter,
      serviceAssociationFilter,
      priorityFilter,
      areaFilter,
      searchQuery,
    ]
  );
  useEffect(() => {
    if (downloadCSV.isPending) {
      toast({
        title: "Report Downloading",
        description: "Your service order report is downloading.",
        variant: "default",
        duration: 3000,
      });
    }
    if (downloadCSV.isSuccess) {
      toast({
        title: "Report Downloaded Successfully",
        description: "Your service order report has been downloaded.",
        variant: "default",
        duration: 3000,
      });
    }

    if (downloadCSV.isError) {
      toast({
        title: "Download Failed",
        description:
          downloadCSV.error?.message ||
          "Failed to download report. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  }, [downloadCSV.isSuccess, downloadCSV.isError, downloadCSV.isPending]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="page-subheading">Assigned Service Orders</h2>
        <p className="page-subtitle">
          Monitor and manage service orders assigned to technicians
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {assignedServiceOrderKpiCards.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            trendDirection={card.trendDirection}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Service Orders Table */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Assigned Service Orders ({pagination?.totalItems || 0})
        </h3>

        <GenericTable
          data={serviceOrders}
          columns={tableColumns}
          rowKey="soId"
          actions={tableActions}
          actionsType="dropdown"
          // Search integration like UserTable
          onSearch={handleSearch}
          searchPlaceholder="Search by SO ID, Name, Reference Number, or Technician..."
          searchParamName="query"
          searchDebounceMs={300}
          // Filter panel integration like UserTable
          filterPanel={
            <ServiceOrderFilterPanel
              currentFilters={filterPanelFilters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              orderType="assigned"
            />
          }
          // Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          onExport={handleExport} // Add this line
          exportLoading={isExporting}
          emptyMessage="No assigned service orders found"
        />
      </div>

      {/* Reassign Technician Modal */}
      <ReassignTechnicianModal
        open={reassignModalOpen}
        onOpenChange={setReassignModalOpen}
        serviceOrder={selectedOrderForReassign}
      />
    </div>
  );
}
